import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

interface RegionItem {
    code?: string;
    countryCode?: string;
    name?: string;
    [key: string]: unknown;
}

interface RegionContextValue {
    selectedCountry: string | null;
    detectedCountry: string | null;
    isLoading: boolean;
    regions: RegionItem[];
    showPopup: boolean;
    confirmRegion: (country: string) => void;
    setShowPopup: React.Dispatch<React.SetStateAction<boolean>>;
}

const RegionContext = createContext<RegionContextValue | undefined>(undefined);

export const useRegion = (): RegionContextValue => {
    const context = useContext(RegionContext);
    if (!context) {
        throw new Error('useRegion must be used within RegionProvider');
    }
    return context;
};

export const RegionProvider = ({ children }: { children: React.ReactNode }) => {
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null); // 'IN', 'US'
    const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [regions, setRegions] = useState<RegionItem[]>([]);
    const [showPopup, setShowPopup] = useState(false);

    // Initial load logic
    useEffect(() => {
        const initRegion = async () => {
            const storedCountry = localStorage.getItem('countryCode');
            // If user already confirmed region in this browser session, skip the popup
            const confirmedThisSession = sessionStorage.getItem('regionConfirmedSession') === 'true';

            try {
                // Call detection API
                const response = await fetch(API_ENDPOINTS.STORE.REGION_DETECT);
                const data = await response.json() as { detectedCountry?: string };
                const detected = data.detectedCountry || 'IN';

                setDetectedCountry(detected);

                if (confirmedThisSession) {
                    // Already confirmed this session - just set the country, no popup
                    setSelectedCountry(storedCountry || detected);
                } else if (!storedCountry) {
                    // Scenario 1: No region stored -> Show popup on store pages
                    setSelectedCountry(detected);
                    setShowPopup(true);
                } else {
                    // Scenario 2: Region stored
                    setSelectedCountry(storedCountry);
                    // Check mismatch
                    if (storedCountry !== detected) {
                        setShowPopup(true);
                    }
                }

                // Fetch supported regions
                const regionResponse = await fetch(API_ENDPOINTS.STORE.REGIONS);
                const regionData = await regionResponse.json() as { success?: boolean; data?: RegionItem[] };
                if (regionData.success) {
                    setRegions(regionData.data || []);
                }
            } catch (error) {
                console.error('Failed to detect region:', error);
                // Fallback
                if (!storedCountry) {
                    setSelectedCountry('IN');
                    localStorage.setItem('countryCode', 'IN');
                } else {
                    setSelectedCountry(storedCountry);
                }
            } finally {
                setIsLoading(false);
            }
        };

        initRegion();
    }, []);

    const confirmRegion = (country: string) => {
        if (country) {
            setSelectedCountry(country);
            localStorage.setItem('countryCode', country);
            // Remember this confirmation for the rest of the session - won't ask again
            sessionStorage.setItem('regionConfirmedSession', 'true');
            setShowPopup(false);
        }
    };

    return (
        <RegionContext.Provider value={{ 
            selectedCountry, 
            detectedCountry, 
            isLoading, 
            regions,
            showPopup, 
            confirmRegion,
            setShowPopup 
        }}>
            {children}
        </RegionContext.Provider>
    );
};

